import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Container, Divider, Image, List } from 'semantic-ui-react'

import SignupHeader from '../SignupHeader'
import {
  Text,
  Card,
  Button,
  GridRowColumn,
  Icon,
  Loader,
  Popup,
} from '../../../components/BaseComponents'
import {
  JOURNEY_SELECTIONS,
  SIGNUP_PATHS,
  STARTER_PLAN_EXPERIMENT_SELECTIONS,
  isFreeTrialEnabled,
} from '../helpers'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '../../../actions/financialProfileActions'
import {
  getFinancialProfile,
  getFreeTrialStatus,
  selectProductDescription,
} from '../../../selectors/user.selectors'
import { useSignupPageUpdate } from '../../../services/onboardingService'
import { useAnalyticsTrack, useAnalyticsView } from '@/featuresV1/Amplitude'
import {
  useFetchResponse,
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '../../../utils/sharedHooks'
import {
  DeviceWidth,
  useIsDeviceWidth,
} from '../../../utils/deviceWidthHelpers'
import { useAppDispatch } from '../../../utils/typeHelpers'
import { useSearchParams } from 'react-router-dom'
import FreeTrialSignupBanner from '../FreeTrialSignupBanner'
import { selectCanOptIntoPreviousTaxYear } from '../../Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '../../Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '../../../features/OpenFeature'
import { getIsFetching, selectIsFetchingForKeys } from '@/reducers/fetch'
import { FinancialProfile } from '@/reducers/auth/userReducer'
import {
  FETCH_STRIPE_PRODUCTS_KEY,
  fetchEligibleStripeProducts,
  PlanTier,
} from '@/actions/settings/billingActions'
import {
  FETCH_PRODUCT_PERMISSIONS_KEY,
  getProductPermissions,
} from '@/actions/permissionActions'
import { PlanDetail } from '../ChoosePlan/helpers'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

const TierSelection = ({
  value,
  imageUrl,
  title,
  subtitle,
  selection,
  setSelection,
  details,
  isStarterPlan = false,
}: {
  value: string
  imageUrl: string
  title: string
  subtitle: string
  selection: string | undefined
  setSelection: (val: string) => void
  details:
    | { title?: string; isVisible: boolean; details: PlanDetail[] }[]
    | null
  isStarterPlan?: boolean
}) => {
  const selected = selection === value
  const isLoadingPermissions = useReselector(selectIsFetchingForKeys, [
    FETCH_PRODUCT_PERMISSIONS_KEY,
    FETCH_STRIPE_PRODUCTS_KEY,
  ])
  const track = useAnalyticsTrack()

  useSignupPageUpdate(SIGNUP_PATHS.practiceJourney)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  return (
    <Grid.Column stretched>
      <Loader loading={isLoadingPermissions} />
      <Card backgroundColor="stone" style={{ minHeight: 620, display: 'flex' }}>
        <Grid>
          <GridRowColumn
            columnStyle={{ display: 'flex', justifyContent: 'center' }}
          >
            <Image src={imageUrl} style={{ height: 200 }} />
          </GridRowColumn>
          <GridRowColumn>
            <Text as="h2" textAlign="center">
              {title}
            </Text>
          </GridRowColumn>
          <GridRowColumn>
            <Text as="h3" textAlign="center">
              {subtitle}
            </Text>
          </GridRowColumn>
          {!isStarterPlan && (
            <>
              <GridRowColumn>
                <Text as="h3">Includes:</Text>
              </GridRowColumn>
              <GridRowColumn short>
                <List>
                  <List.Content>
                    <List.Item
                      style={{
                        display: 'flex',
                        gap: 16,
                        marginBottom: 12,
                        alignItems: 'center',
                      }}
                    >
                      <Icon color="green" size="1x" icon={regular('check')} />
                      <Text as="bodyLg">Everything in Starter Plan</Text>
                    </List.Item>
                  </List.Content>
                </List>
              </GridRowColumn>
            </>
          )}
          {details?.map(
            (d) =>
              d.isVisible && (
                <>
                  <GridRowColumn key={d.title}>
                    <Text as="h2">{d.title}</Text>
                  </GridRowColumn>
                  <GridRowColumn short>
                    <List>
                      <List.Content>
                        {d.details.map((detail) => (
                          <List.Item
                            key={detail.text}
                            style={{
                              display: 'flex',
                              gap: 16,
                              marginBottom: 12,
                              alignItems: 'center',
                            }}
                          >
                            <Icon
                              color="green"
                              size="1x"
                              icon={regular('check')}
                            />
                            <Text as="bodyLg">
                              {detail.text}{' '}
                              {detail.tooltip && (
                                <Popup
                                  content={detail.tooltip}
                                  trigger={
                                    <Icon
                                      icon={regular('question-circle')}
                                      color="mediumGray"
                                    />
                                  }
                                />
                              )}
                            </Text>
                          </List.Item>
                        ))}
                      </List.Content>
                    </List>
                  </GridRowColumn>
                </>
              )
          )}
        </Grid>
        {!isMobile && <div style={{ flex: 1 }} />}
        <Grid>
          <GridRowColumn>
            <Button
              style={{ marginTop: 28 }}
              variant={selected ? 'primary' : 'secondary'}
              onClick={() => {
                setSelection(value)
                track(`clicked ${value} in practice-journey`)
              }}
              fullWidth
            >
              {selected ? 'Selected' : 'Select'}
            </Button>
          </GridRowColumn>
        </Grid>
      </Card>
    </Grid.Column>
  )
}

const JourneySelection = ({
  value,
  imageUrl,
  title,
  subtitle,
  selection,
  setSelection,
  recs,
}: {
  value: string
  imageUrl: string
  title: string
  subtitle: string
  selection: string | undefined
  setSelection: (val: string) => void
  recs: string[]
}) => {
  const selected = selection === value

  useSignupPageUpdate(SIGNUP_PATHS.practiceJourney)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  return (
    <Grid.Column>
      <Card backgroundColor="stone" style={{ minHeight: 620, display: 'flex' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column style={{ display: 'flex', justifyContent: 'center' }}>
              <Image src={imageUrl} style={{ height: 200 }} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Text as="h2" textAlign="center">
                {title}
              </Text>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Text as="h3" textAlign="center">
                {subtitle}
              </Text>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Text as="bodyLg">You have:</Text>
              <ul style={{ marginTop: 0 }}>
                {recs.map((rec) => (
                  <li key={rec}>
                    <Text as="bodyLg">{rec}</Text>
                  </li>
                ))}
              </ul>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {!isMobile && <div style={{ flex: 1 }} />}
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <Button
                variant={selected ? 'primary' : 'secondary'}
                onClick={() => setSelection(value)}
                fullWidth
              >
                {selected ? 'Selected' : 'Select'}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Card>
    </Grid.Column>
  )
}

const PracticeJourney = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const pageView = useAnalyticsView()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const [searchParams] = useSearchParams()
  const freeTrialSearchParamValue = searchParams.get('trial')
  const basicPlanSearchParamValue = searchParams.get('subscription')
  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const freeTrialEnabled = isFreeTrialEnabled(
    freeTrialSearchParamValue,
    userFreeTrialStatus,
    useBasicTypePlan
  )

  useEffect(() => {
    pageView('sign up practice journey')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  const financialProfile = useReselector(getFinancialProfile)
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )

  const isSubmitting = useReselector(
    getIsFetching,
    UPDATE_FINANCIAL_PROFILE_KEY(financialProfile?.id)
  )

  const [selection, setSelection] = useState(
    financialProfile?.practiceType || ''
  )
  const eligiblePlans = useFetchResponse(fetchEligibleStripeProducts)
  const eligibleStarterPlans = useMemo(
    () =>
      eligiblePlans?.filter((plan) => plan.metadata?.tier === PlanTier.starter),
    [eligiblePlans]
  )
  const eligibleStandardPlans = useMemo(
    () =>
      eligiblePlans?.filter(
        (plan) =>
          plan.metadata?.tier === PlanTier.essential ||
          plan.metadata?.tier === PlanTier.premium
      ),
    [eligiblePlans]
  )
  const standardPlanTier = useMemo(
    () => eligibleStandardPlans?.[0]?.metadata?.tier,
    [eligibleStandardPlans]
  )
  const starterPlanIds = useMemo(
    () => eligibleStarterPlans?.map((plan) => plan.id) || [],
    [eligibleStarterPlans]
  )
  const starterPlanPermissions = useFetchResponse(getProductPermissions, {
    productIds: starterPlanIds,
  })
  // essential or premium
  const standardPlanIds = useMemo(
    () => eligibleStandardPlans?.map((plan) => plan.id) || [],
    [eligibleStandardPlans]
  )
  const standardPlanPermissions = useFetchResponse(getProductPermissions, {
    productIds: standardPlanIds,
  })
  const [planTier, setPlanTier] = useState<string | undefined>(
    localStorage.getItem('planTier') ?? undefined
  )
  const enableStarterPlans = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStarterPlans,
    false
  )
  const starterPlanDetails = useReselector(selectProductDescription, {
    sections: ['bookkeeping', 'reporting', 'support'],
    isStarterPlanEnabled: enableStarterPlans,
    permissions: starterPlanPermissions?.[starterPlanIds[0]],
  })
  // essential or premium
  const standardPlanDetails = useReselector(selectProductDescription, {
    sections: ['taxes', 'scorp', 'group', 'support'],
    isStarterPlanEnabled: enableStarterPlans,
    permissions: standardPlanPermissions?.[standardPlanIds[0]],
  })

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const handleSubmit = useCallback(async () => {
    let res: FinancialProfile | boolean | undefined
    if (enableStarterPlans) {
      if (planTier) {
        localStorage.setItem('planTier', planTier)
        res = true
      }
    } else {
      res = await updateFinancialProfile(financialProfile?.id, {
        practiceType: selection,
        practiceSize: selection === 'group' ? '2-10' : '1',
      })(dispatch)
    }
    /**
     * If free trial, skip tax
     *
     */
    if (res) {
      if (freeTrialSearchParamValue || useBasicTypePlan) {
        navigate(SIGNUP_PATHS.choosePlan)
      } else {
        if (canOptIntoPreviousYearTaxes) {
          navigate(SIGNUP_PATHS.previousYearTax)
        } else {
          navigate(SIGNUP_PATHS.choosePlan)
        }
      }
    }
  }, [
    canOptIntoPreviousYearTaxes,
    dispatch,
    financialProfile?.id,
    freeTrialSearchParamValue,
    navigate,
    useBasicTypePlan,
    planTier,
    selection,
    enableStarterPlans,
  ])

  return (
    <Container style={{ paddingTop: 64 }}>
      <Grid stackable>
        <span ref={scrollRef} />
        <SignupHeader currentStep={2} />
        <GridRowColumn>
          {freeTrialEnabled && <FreeTrialSignupBanner />}
        </GridRowColumn>
        <GridRowColumn>
          {enableStarterPlans ? (
            <Text as="h1">Choose a plan</Text>
          ) : (
            <Text as="h1">Where are you in your private practice journey?</Text>
          )}
        </GridRowColumn>
        <GridRowColumn short>
          {enableStarterPlans ? (
            <Text as="bodyLg">
              Based upon your answers, these are the plans that work for your
              practice. Choose what best meets your needs.
            </Text>
          ) : (
            <Text as="bodyLg">
              Choose the option that is closest to your current practice.
            </Text>
          )}
        </GridRowColumn>
        {enableStarterPlans ? (
          <Grid.Row columns="equal">
            <TierSelection
              {...STARTER_PLAN_EXPERIMENT_SELECTIONS['starter']}
              setSelection={setPlanTier}
              selection={planTier}
              details={starterPlanDetails}
              isStarterPlan
            />
            <TierSelection
              {...STARTER_PLAN_EXPERIMENT_SELECTIONS[
                standardPlanTier === PlanTier.premium ? 'premium' : 'essential'
              ]}
              setSelection={setPlanTier}
              selection={planTier}
              details={standardPlanDetails}
            />
          </Grid.Row>
        ) : (
          <Grid.Row columns="equal">
            <JourneySelection
              {...JOURNEY_SELECTIONS['gettingStarted']}
              setSelection={setSelection}
              selection={selection}
            />
            <JourneySelection
              {...JOURNEY_SELECTIONS['soloPractice']}
              setSelection={setSelection}
              selection={selection}
            />
            <JourneySelection
              {...JOURNEY_SELECTIONS['groupPractice']}
              setSelection={setSelection}
              selection={selection}
            />
          </Grid.Row>
        )}

        <Divider />
        {!isMobile && (
          <Grid.Row>
            <Grid.Column width={3}>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => navigate(SIGNUP_PATHS.aboutYourPractice)}
              >
                Back
              </Button>
            </Grid.Column>
            <Grid.Column width={10} />
            <Grid.Column width={3}>
              <Button
                fullWidth
                onClick={handleSubmit}
                disabled={(!selection && !planTier) || isSubmitting}
                id="btn-signup-practice-type"
                loading={isSubmitting}
              >
                Continue
              </Button>
            </Grid.Column>
          </Grid.Row>
        )}
        {isMobile && (
          <Grid.Row>
            <Grid.Column>
              <Button
                fullWidth
                onClick={handleSubmit}
                disabled={(!selection && !planTier) || isSubmitting}
                id="btn-signup-practice-type"
                loading={isSubmitting}
              >
                Continue
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => navigate(SIGNUP_PATHS.aboutYourPractice)}
              >
                Back
              </Button>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Container>
  )
}

export default PracticeJourney
